import { UserManager, User, UserManagerSettings } from "oidc-client-ts";
import { oidcSettings } from "./oidcSettings";

class AuthService {
  private manager: UserManager;
  private currentUser: User | null = null;
  private _signinCallbackPromise: Promise<User> | null = null;

  constructor(settings: UserManagerSettings) {
    this.manager = new UserManager(settings);
  }

  get userManager() {
    return this.manager;
  }

  async getUser(): Promise<User | null> {
    this.currentUser = await this.manager.getUser();
    return this.currentUser;
  }

  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return !!user && !user.expired;
  }

  async getAccessToken(): Promise<string | null> {
    const user = await this.getUser();
    return user?.access_token ?? null;
  }

  signIn(returnUrl?: string, prompt?: string) {
    return this.manager.signinRedirect({
      state: returnUrl,
      ...(prompt ? { prompt } : {}),
    });
  }

  async completeSignIn(): Promise<User> {
    if (!this._signinCallbackPromise) {
      this._signinCallbackPromise = this.manager.signinRedirectCallback().then(
        (user) => {
          this.currentUser = user;
          this._signinCallbackPromise = null;
          return user;
        },
        (err) => {
          this._signinCallbackPromise = null;
          throw err;
        }
      );
    }
    return this._signinCallbackPromise;
  }

  async signOut() {
    // Only clear local OIDC state. Do NOT use signoutRedirect — it is
    // unreliable across IDPs and causes redirect loops. The login page
    // handles forcing re-auth via prompt=login.
    await this.manager.removeUser();
  }
}

export const authService = new AuthService(oidcSettings);
